import React from 'react';
import { Button, Typography, Layout, Tag, Card, Space, Divider, Row, Col, Avatar, Grid, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CrownOutlined, CreditCardOutlined, StarOutlined, HeartOutlined, RocketOutlined } from '@ant-design/icons';
import createCustomerPortalSession from '../utils/stripe/createCustomerPortalSession';
import getCurrentSubscription from '../utils/stripe/getCurrentSubscription';
import { toast } from 'react-hot-toast';
import { logEvent } from '../analytics';
import SubscriptionSuccessAlert from '../components/subscription/SubscriptionSuccessAlert';
import SubscriptionPendingCancellationAlert from '../components/subscription/SubscriptionPendingCancellationAlert';
import SubscriptionPendingChangeAlert from '../components/subscription/SubscriptionPendingChangeAlert';
import SubscriptionProblemAlert from '../components/subscription/SubscriptionProblemAlert';
import FAQ from '../components/FAQ';
import ClimatePledgeCard from '../components/subscription/ClimatePledgeCard';
import BackLink from '../components/BackLink';

const { Text, Title, Paragraph } = Typography;
const { Content } = Layout;
const { useBreakpoint } = Grid;

// Reusable value badge component
const ValueBadge = ({ icon: Icon, text }) => {  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '14px',
      padding: '8px 16px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      <Icon /> {text}
    </div>
  );
};

// Reusable subscription detail card component
const SubscriptionDetailCard = ({ label, value, isTag = false, tagColor = 'green', icon: Icon }) => (
  <Col xs={24} sm={12} md={6}>
    <div style={{ 
      textAlign: 'center', 
      padding: '16px',
      background: 'linear-gradient(135deg, #f8fbff 0%, #faf5ff 100%)',
      borderRadius: '8px',
      border: '1px solid #e3f2fd',
      color: '#333'
    }}>
      <Text style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}>
        {label}
      </Text>
      {isTag ? (
        <Tag color={tagColor} style={{ fontSize: '14px', padding: '2px 8px' }}>
          {value}
        </Tag>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {Icon && <Icon style={{ fontSize: '16px', color: '#4c1d95' }} />}
          <Text strong style={{ fontSize: '16px', color: '#4c1d95' }}>
            {value}
          </Text>
        </div>
      )}
    </div>
  </Col>
);

const Subscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [loading, setLoading] = React.useState(false);
  const [subscription, setSubscription] = React.useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = React.useState(true);
  const isMobile = !screens.md;

  // Helper function to get date field info based on subscription state
  const getDateFieldInfo = () => {
    if (subscription?.hasPendingCancellation()) {
      return {
        label: t('subscription.fields.cancellation_date'),
        value: subscription?.getPendingCancellationDate() || 'N/A'
      };
    }
    
    if (subscription?.getPendingPlanName()) {
      return {
        label: t('subscription.fields.plan_change_date'),
        value: subscription?.getPendingPlanEffectiveDate() || 'N/A'
      };
    }
    
    return {
      label: t('subscription.fields.next_billing_date'),
      value: subscription?.getNextBillingDate() || 'N/A'
    };
  };

  // Fixed set of 3 icons for all plans
  const planIcons = [RocketOutlined, StarOutlined, HeartOutlined];

  // Fetch current subscription data
  React.useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscription = await getCurrentSubscription();
        setSubscription(subscription);
      } catch (error) {
        console.error('Error fetching subscription:', error);
        toast.error(t('subscription.error_fetching', 'Error fetching subscription data. Please try again.'));
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  // Redirect to pricing if user doesn't have a subscription
  React.useEffect(() => {
    if (!subscriptionLoading && subscription && !subscription.hasSubscription()) {
      navigate('/pricing', { replace: true });
    }
  }, [subscription, subscriptionLoading, navigate]);

  const planName = subscription?.getPlanName();
  const planDisplayName = subscription?.getPlanDisplayName();
  const subscriptionStatus = subscription?.getStatus();
  const billingPeriod = subscription?.getBillingPeriod();

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      logEvent('Subscription', 'Manage');
      
      const response = await createCustomerPortalSession();
      console.log('Customer portal session response:', response);
      
      if (!response || !response.url) {
        throw new Error('Invalid response from server - no URL provided');
      }
      
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      toast.error(t('subscription.error_creating_portal', 'Error accessing billing portal. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (subscriptionLoading) {
    return (
      <Content className="page">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }} data-testid="subscription-skeleton">
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </Content>
    );
  }

  return (
    <Content className="page subscription-page">
      {isMobile && (
        <Row align="middle" gutter={[16, 16]} style={{ marginBottom: '8px', marginLeft: '0px' }}>
          <Col flex="none" style={{ textAlign: 'left', paddingLeft: 0 }}>
            <BackLink to="/" label={t('common.back')} />
          </Col>
        </Row>
      )}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Hero Section - Enhanced with value proposition */}
        <Card 
          style={{ 
            marginBottom: '32px', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Avatar src="https://img.icons8.com/plasticine/100/heart-with-pulse.png" size={80} />
            <Title level={2} style={{ color: 'white', margin: '12px 0' }}>
              <span style={{ display: screens.md ? 'inline' : 'none' }}>
                {t('subscription.hero_title')}
              </span>
              <span style={{ display: screens.md ? 'none' : 'inline' }}>
                {t('subscription.hero_title_mobile')}
              </span>
            </Title>
            
            {/* Value badges */}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginTop: '40px' }}>
              {planName && (
                <>
                  <ValueBadge icon={planIcons[0]} text={t(`subscription.plan_values.${planName}.bullet1`)} />
                  <ValueBadge icon={planIcons[1]} text={t(`subscription.plan_values.${planName}.bullet2`)} />
                  <ValueBadge icon={planIcons[2]} text={t(`subscription.plan_values.${planName}.bullet3`)} />
                </>
              )}
            </div>
          </div>
        </Card>

        <>
          {/* Pending Cancellation Alert */}
          {subscription?.hasPendingCancellation() && (
            <SubscriptionPendingCancellationAlert />
          )}
          
          {/* Pending Change Alert */}
          {!subscription?.hasPendingCancellation() && 
            subscription?.getPendingPlanName() && (
            <SubscriptionPendingChangeAlert 
              pendingPlanName={subscription?.getPendingPlanDisplayName()}
              effectiveDate={subscription?.getPendingPlanEffectiveDate()}
            />
          )}

          {/* Past Due or Unpaid Alert */}
          {subscription?.hasSubscriptionProblem() && (
            <SubscriptionProblemAlert />
          )}
          
          {/* Success Alert - Default when no pending actions */}
          {!subscription?.hasPendingCancellation() && !subscription?.getPendingPlanName() && (
            <SubscriptionSuccessAlert />
          )}
        </>

        {/* Current Plan Card - Enhanced with positive messaging */}
        <Card 
          title={
            <Text strong style={{ fontSize: '18px' }}>{t('subscription.title')}</Text>
          }
          style={{ 
            marginBottom: '32px',
            border: '2px solid #f8f9fa',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
          }}
          styles={{
            header: {
              backgroundColor: '#fefefe',
              borderBottom: '2px solid #f0f0f0',
              padding: '12px 24px'
            }
          }}
          data-testid="subscription-details-card"
        >
          {/* Plan Details - Clean Layout */}
          <div style={{ padding: '16px 0' }}>
            <Row gutter={[16, 16]}>
              <SubscriptionDetailCard 
                label={t('subscription.fields.plan_name')}
                value={planDisplayName || 'N/A'}
                icon={CrownOutlined}
              />
              
              <SubscriptionDetailCard 
                label={t('subscription.fields.status')}
                value={t(`subscription.statuses.${subscriptionStatus}`)}
                isTag={true}
                tagColor={subscriptionStatus === 'active' ? 'green' : 'orange'}
              />
              
              <SubscriptionDetailCard 
                label={t('subscription.fields.billing_period')}
                value={t(`subscription.periods.${billingPeriod}`)}
              />
              
              <SubscriptionDetailCard 
                label={getDateFieldInfo().label}
                value={getDateFieldInfo().value}
              />
            </Row>
          </div>
          
          <Divider style={{ margin: '32px 0' }} />
          
          <div style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="large">
              <Paragraph style={{ margin: '0', color: '#999', fontSize: '16px' }}>
                {t('subscription.manage_description')}
              </Paragraph>
              <Button 
                type="primary" 
                size="large"
                loading={loading}
                icon={<CreditCardOutlined />}
                onClick={handleManageSubscription}
                style={{ backgroundColor: 'var(--upgrade-color)', borderColor: 'var(--upgrade-color)' }}
                data-testid="manage-subscription-button"
              >
                {t('subscription.manage_call_to_action')}
              </Button>
            </Space>
          </div>
        </Card>

        {/* FAQ Section */}
        <FAQ localeKey="faq_subscription" />

        {/* Climate Pledge (bottom of page) */}
        <ClimatePledgeCard />
      </div>
    </Content>
  );
};

export default Subscription;